/**
 * Sign up (/signup) — email, password, confirm password. Nothing else.
 *
 * Everything about the person (name, school, board, grade) is asked for AFTER
 * the email is confirmed, in /onboarding. Keeping this form to three fields is
 * deliberate: a student who abandons a long form has given us an unverified
 * address and nothing to show for it, while one who gets past this screen is
 * already signed in and can be walked through the rest.
 *
 * Better Auth creates the account, signs it in, and emails a verification link,
 * so the next screen is /verify-email with the message already on its way.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ds';
import {
  AUTH_BUTTON_STYLE,
  AuthError,
  AuthHeading,
  AuthLayout,
  TextField,
} from '@/components/auth/AuthLayout';
import { MIN_PASSWORD_LENGTH, signUp } from '@/lib/session';

export function SignupPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Field errors appear only after a submit attempt: flagging "too short" while
  // someone is still typing is just nagging.
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const passwordError =
    password.length > 0 && password.length < MIN_PASSWORD_LENGTH
      ? `Use at least ${MIN_PASSWORD_LENGTH} characters.`
      : '';
  const confirmError = confirm.length > 0 && confirm !== password ? 'Passwords do not match.' : '';
  const canSubmit =
    email.trim().length > 3 &&
    password.length >= MIN_PASSWORD_LENGTH &&
    confirm === password &&
    !busy;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setShowFieldErrors(true);
    if (!canSubmit) return;

    setBusy(true);
    setError('');
    try {
      await signUp({ email: email.trim(), password });
      navigate('/verify-email', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create your account.');
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
        <AuthHeading title="Create your account">
          It takes a minute. We&rsquo;ll confirm your email, then set up your subjects.
        </AuthHeading>

        <AuthError>{error}</AuthError>

        <TextField
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          hint="We'll send a link here to confirm it's yours."
        />

        <TextField
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
          error={showFieldErrors ? passwordError : ''}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters, and not your email address.`}
        />

        <TextField
          label="Confirm password"
          type="password"
          name="confirmPassword"
          autoComplete="new-password"
          required
          placeholder="Type it again"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={busy}
          error={showFieldErrors || confirm.length > 0 ? confirmError : ''}
        />

        <Button type="submit" style={AUTH_BUTTON_STYLE} disabled={busy}>
          {busy ? 'Creating your account…' : 'Create account'}
        </Button>

        <p className="text-center text-sm text-ink-soft">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-semibold text-forest underline-offset-2 hover:underline"
          >
            Sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export default SignupPage;
